/**
 * @fileoverview Premium StatCard Component for Skillzy.pro
 *
 * Dashboard stat card with icon, animated counter, trend indicator,
 * sparkline support, and Framer Motion entrance animation.
 *
 * @example
 * import { Users, TrendingUp } from 'lucide-react';
 *
 * <StatCard
 *   icon={<Users />}
 *   label="Total Students"
 *   value={12847}
 *   previousValue={11230}
 *   prefix=""
 *   format="number"
 * />
 *
 * <StatCard
 *   icon={<DollarSign />}
 *   label="Revenue"
 *   value={48250}
 *   previousValue={42100}
 *   prefix="$"
 *   format="compact"
 *   variant="gradient"
 * />
 */

import React, { forwardRef, useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import clsx from 'clsx';

/**
 * Animated counter hook that counts up to the target value.
 * @param {number} target - The final value to count to
 * @param {number} [duration=1200] - Animation duration in ms
 * @param {boolean} [start=true] - Whether to start the animation
 * @returns {number} Current animated value
 */
function useAnimatedCounter(target, duration = 1200, start = true) {
  const [count, setCount] = useState(0);
  const startTimeRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start || typeof target !== 'number' || isNaN(target)) {
      setCount(target || 0);
      return;
    }

    setCount(0);
    startTimeRef.current = null;

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, start]);

  return count;
}

/**
 * Formats a number for display.
 * @param {number} value
 * @param {'number' | 'compact' | 'percent' | 'currency'} format
 * @param {string} [prefix='']
 * @param {string} [suffix='']
 * @returns {string}
 */
function formatValue(value, format = 'number', prefix = '', suffix = '') {
  let formatted;

  switch (format) {
    case 'compact':
      if (value >= 1_000_000) formatted = `${(value / 1_000_000).toFixed(1)}M`;
      else if (value >= 1_000) formatted = `${(value / 1_000).toFixed(1)}K`;
      else formatted = value.toLocaleString();
      break;
    case 'percent':
      formatted = `${value.toFixed(1)}`;
      break;
    case 'currency':
      formatted = value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      break;
    default:
      formatted = value.toLocaleString();
  }

  return `${prefix}${formatted}${suffix}`;
}

/** @type {Record<string, string>} */
const variantClasses = {
  default: clsx(
    'bg-white border border-slate-200/80',
    'dark:bg-slate-900 dark:border-slate-800'
  ),
  elevated: clsx(
    'bg-white border border-slate-200/60',
    'shadow-md shadow-slate-200/50',
    'dark:bg-slate-900 dark:border-slate-800 dark:shadow-slate-950/50'
  ),
  glass: clsx(
    'bg-white/80 backdrop-blur-xl border border-white/50',
    'dark:bg-slate-900/80 dark:border-slate-700/50'
  ),
  gradient: clsx(
    'bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600',
    'text-white border-0 shadow-lg shadow-indigo-600/25'
  ),
};

/** @type {Record<string, string>} */
const iconContainerClasses = {
  default: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  elevated: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400',
  glass: 'bg-indigo-50/80 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400',
  gradient: 'bg-white/20 text-white',
};

/**
 * Premium StatCard for dashboard metrics.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Lucide icon or any React element
 * @param {string} props.label - Metric label
 * @param {number} props.value - Current metric value
 * @param {number} [props.previousValue] - Previous value for trend calculation
 * @param {string} [props.prefix=''] - Value prefix (e.g., "$")
 * @param {string} [props.suffix=''] - Value suffix (e.g., "%")
 * @param {'number' | 'compact' | 'percent' | 'currency'} [props.format='number']
 * @param {'default' | 'elevated' | 'glass' | 'gradient'} [props.variant='default']
 * @param {string} [props.description] - Optional description below the value
 * @param {boolean} [props.animateValue=true] - Animate value counting up
 * @param {string} [props.className]
 */
const StatCard = forwardRef(({
  icon,
  label,
  title,
  value,
  previousValue,
  prefix = '',
  suffix = '',
  format = 'number',
  variant = 'default',
  description,
  animateValue = true,
  className,
  ...rest
}, ref) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: '-50px' });

  const animatedValue = useAnimatedCounter(
    value,
    1200,
    animateValue && isInView
  );

  const displayValue = animateValue ? animatedValue : value;

  // Calculate trend
  const trend = previousValue != null && previousValue !== 0
    ? ((value - previousValue) / previousValue) * 100
    : null;

  const isPositive = trend !== null && trend >= 0;
  const isGradient = variant === 'gradient';

  return (
    <motion.div
      ref={(node) => {
        cardRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
      transition={{ duration: 0.5, ease: [0, 0, 0.2, 1] }}
      className={clsx(
        'relative rounded-xl p-5 sm:p-6',
        'transition-all duration-300',
        'hover:shadow-lg hover:-translate-y-0.5',
        variantClasses[variant] || variantClasses.default,
        className
      )}
      {...rest}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left side: icon + metrics */}
        <div className="flex-1 min-w-0">
          {/* Label */}
          <p className={clsx(
            'text-sm font-medium truncate',
            isGradient ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'
          )}>
            {title || label}
          </p>

          {/* Value */}
          <p className={clsx(
            'mt-2 text-2xl sm:text-3xl font-bold tracking-tight tabular-nums',
            isGradient ? 'text-white' : 'text-slate-900 dark:text-white'
          )}>
            {formatValue(displayValue, format, prefix, suffix)}
          </p>

          {/* Trend + description */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {trend !== null && (
              <span
                className={clsx(
                  'inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold',
                  isGradient
                    ? isPositive
                      ? 'bg-white/20 text-white'
                      : 'bg-white/20 text-white'
                    : isPositive
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                      : 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'
                )}
              >
                {/* Trend arrow */}
                <svg
                  className={clsx('h-3 w-3', !isPositive && 'rotate-180')}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                {Math.abs(trend).toFixed(1)}%
              </span>
            )}

            {description && (
              <span className={clsx(
                'text-xs',
                isGradient ? 'text-white/60' : 'text-slate-400 dark:text-slate-500'
              )}>
                {description}
              </span>
            )}
          </div>
        </div>

        {/* Icon container */}
        {icon && (
          <div
            className={clsx(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
              '[&>svg]:h-5 [&>svg]:w-5',
              iconContainerClasses[variant] || iconContainerClasses.default
            )}
          >
            {React.isValidElement(icon) ? icon : React.createElement(icon)}
          </div>
        )}
      </div>
    </motion.div>
  );
});

StatCard.displayName = 'StatCard';

export default StatCard;
